import { useMemo } from 'react'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Note, NoteStatusStates } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'

import { DA_FLEXIBLE_NOTE_NAME } from '../utils/noteConstants'

/**
 * Return true if:
 * - appointment is completed OR
 * - appointment is concluded (regardless of reversal status)
 *
 * Reversed-charge concluded appointments must still be included so their notes
 * (which may be reverted to draft for addendum) remain visible in the notes list.
 * @param appt appointment to be checked
 */
const isCompletedOrConcluded = (appt: Appointment) =>
  appt?.appointmentStatus === 'completed' || appt?.appointmentStatus === 'concluded'

/**
 * Return true if the appointment is a past unclosed session that is eligible for the DA Flexible Note flow.
 * Aligned with the calendar's useGetCloseSessionType logic:
 * - status is newAppt or rescheduled (unclosed)
 * - has a programId (not a legacy DA appointment)
 * - is not missed or canceled
 * - is in the past
 */
const isPastUnclosedSession = (appt: Appointment) => {
  const isUnclosed = appt?.appointmentStatus === 'newAppt' || appt?.appointmentStatus === 'rescheduled'
  const hasProgram = !!appt?.programId
  return isUnclosed && hasProgram && getDateFromAppointment(appt) < new Date()
}

const appointmentsForEpisode = (episodeId: string | number) => (appointment: Appointment) =>
  appointment.episodeId === episodeId

/**
 * Returns a Set of appointmentIds for DA Engage providers where the session is completed
 * or concluded (including reversed-charge sessions that need addendum notes).
 *
 * When includeUnclosedPastAppts is true (unified flow FF), also includes past unclosed sessions
 * (newAppt/rescheduled) that have a programId, matching the calendar's eligibility logic.
 */
export const useCompletedOrResubmittedAppts = (
  allAppointments?: Appointment[] | null,
  episodeId?: string | number,
  includeUnclosedPastAppts = false,
) => {
  return useMemo(() => {
    if (!allAppointments || !episodeId) {
      return new Set<string | number>()
    }

    const apptsInEpisode = (allAppointments ?? []).filter(appointmentsForEpisode(episodeId))

    const eligibleApptIds = apptsInEpisode
      .filter((appt) => isCompletedOrConcluded(appt) || (includeUnclosedPastAppts && isPastUnclosedSession(appt)))
      .map((appt: Appointment) => appt?.appointmentId)

    return new Set<string | number>(eligibleApptIds)
  }, [allAppointments, episodeId, includeUnclosedPastAppts])
}

export const useAppointmentsThatNeedNotes = (
  allAppointments?: Appointment[] | null,
  notes?: Note[] | null,
  episodeId?: string | number,
  isFlexibleNoteProvider = false,
) => {
  return useMemo(() => {
    if (!allAppointments || !episodeId) {
      return new Set<string | number>()
    }

    const apptsInEpisode = (allAppointments ?? []).filter(appointmentsForEpisode(episodeId))

    const completedNoteApptIds = new Set(
      (notes ?? []).filter((n) => n?.status === NoteStatusStates.COMPLETED).map((n) => n?.appointment_id),
    )
    const oldFlowNoteApptIds = new Set(
      (notes ?? []).filter((n) => n?.content_meta_data?.name !== DA_FLEXIBLE_NOTE_NAME).map((n) => n?.appointment_id),
    )

    const apptsIntEpisodeWithNoNote = apptsInEpisode
      .filter((appt) => {
        if (isCompletedOrConcluded(appt)) {
          return !completedNoteApptIds.has(appt?.appointmentId)
        }
        if (isFlexibleNoteProvider && isPastUnclosedSession(appt)) {
          // Align with ClientNotes.tsx: only include unclosed past sessions that have no note at all
          // or have a draft of the NEW flexible note type.
          // If a draft note already exists from the old daSessionNote flow, this session
          // should not appear in the banner via the flex path.
          return !completedNoteApptIds.has(appt?.appointmentId) && !oldFlowNoteApptIds.has(appt?.appointmentId)
        }
        return false
      })
      .map((appt: Appointment) => appt?.appointmentId)

    return new Set<string | number>(apptsIntEpisodeWithNoNote)
  }, [allAppointments, episodeId, notes, isFlexibleNoteProvider])
}
